/**
 * Role Activation 2FA Modal
 *
 * Modal component for entering 2FA verification code during role activation.
 * Displays a 6-digit code input field with countdown timer and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import clsx from 'clsx';

export interface RoleActivation2FAModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Masked email destination where the code was sent */
  destination: string;
  /** Expiration time in seconds */
  expiresIn: number;
  /** Remaining verification attempts */
  attemptsRemaining: number;
  /** Callback when user submits the code */
  onSubmit: (code: string) => Promise<void>;
  /** Whether the verification is in progress */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format remaining seconds as "Xm Xs"
 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

export const RoleActivation2FAModal = ({
  isOpen,
  onClose,
  destination,
  expiresIn,
  attemptsRemaining,
  onSubmit,
  isLoading = false,
  error = null,
}: RoleActivation2FAModalProps) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(expiresIn);
  const [resetKey, setResetKey] = useState(0);

  // Reset state when modal opens by incrementing reset key
  // This avoids direct setState in effect which causes lint warnings
  useEffect(() => {
    if (isOpen) {
      setResetKey((k) => k + 1);
    }
  }, [isOpen]);

  // Reset form when resetKey changes
  useEffect(() => {
    if (resetKey > 0) {
      setCode('');
      setLocalError(null);
      setRemainingTime(expiresIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || remainingTime <= 0) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, remainingTime]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate code
      if (!code || code.length !== 6) {
        setLocalError('Please enter a 6-digit verification code');
        return;
      }

      if (!/^\d{6}$/.test(code)) {
        setLocalError('Verification code must be 6 digits');
        return;
      }

      if (remainingTime <= 0) {
        setLocalError('Verification code has expired');
        return;
      }

      setLocalError(null);

      try {
        await onSubmit(code);
        // Success - modal will be closed by parent component
      } catch (err) {
        // Error will be passed via error prop from parent
        console.error('[RoleActivation2FAModal] Verification failed:', err);
      }
    },
    [code, remainingTime, onSubmit]
  );

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      setLocalError(null);
    }
  }, []);

  const displayError = error || localError;
  const isExpired = remainingTime <= 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" closeOnOverlayClick={!isLoading}>
      <form onSubmit={handleSubmit}>
        <Modal.Header title="Two-Factor Authentication Required" />
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              A verification code has been sent to <strong>{destination}</strong>.
            </p>

            <div className="space-y-2">
              <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Verification Code
              </label>
              <input
                id="verification-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                disabled={isLoading || isExpired}
                placeholder="000000"
                autoComplete="off"
                autoFocus
                className={clsx(
                  'block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest rounded-lg border shadow-sm',
                  'focus:outline-none focus:ring-2',
                  isExpired || displayError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                  'dark:bg-gray-700 dark:border-gray-600 dark:text-white',
                  (isLoading || isExpired) && 'opacity-50 cursor-not-allowed'
                )}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Enter the 6-digit code
              </p>
            </div>

            {/* Timer and attempts info */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 dark:text-gray-400">Expires in:</span>
                <span
                  className={clsx(
                    'font-medium',
                    isExpired
                      ? 'text-red-600 dark:text-red-400'
                      : remainingTime <= 30
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {formatTime(remainingTime)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 dark:text-gray-400">Attempts:</span>
                <span
                  className={clsx(
                    'font-medium',
                    attemptsRemaining <= 2
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {attemptsRemaining} left
                </span>
              </div>
            </div>

            {/* Error message */}
            {displayError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{displayError}</p>
              </div>
            )}

            {/* Expired warning */}
            {isExpired && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This verification code has expired. Please close this dialog and try activating the role again.
                </p>
              </div>
            )}

            {/* Low attempts warning */}
            {attemptsRemaining <= 2 && !isExpired && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Warning: Only {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before this code is locked.
                </p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isExpired || code.length !== 6}
            className={clsx(
              'px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500',
              'bg-primary-600 hover:bg-primary-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
